import { ArrowDown, ArrowUp, SelectOption, Tooltip } from '@dtx-company/shared-components/src'
import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Button } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { CheckBox } from '@dtx-company/shared-components/src/components/atoms/Checkbox/index'
import { ConditionalRender } from '../../../utils/conditional-render'
import { CreateFlowcodeContainerState } from '../../../../types/create-flowcode.types'
import { DEFAULT_DOWNLOAD_OPTION, FILE_TYPE_TOOLTIPS } from '../../../../constants/flowcode'
import { FC, useState } from 'react'
import { FlowcodeDownloadOptionMimeLabel } from '@app/code/src/types/flowcode.types'
import { FlowcodeDownloadOptionMimeType } from '@dtx-company/inter-app/src/types/flowcode'
import { HeapContainers } from '../../../../constants/heap'
import { Section } from '@dtx-company/shared-components/src/components/atoms/Section/index'
import { Select } from '@dtx-company/shared-components/src/components/atoms/Select/index'
import { SimplifiedDesktop } from '../../SimplifiedDesktop/SimplifiedDesktop'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { questionMarkBlue } from '../../../../assets'
import {
  updateFileTypeToDownload,
  updateFlowcodeOptionsTransparency
} from '@dtx-company/inter-app/src/redux/slices/homePageSlice'
import { useCreateFlowcodeState } from '../../../../hooks/useCreateFlowcodeState'
import { useCreateFlowcodeStateActions } from '../CreateFlowcodeForm.hooks'
import { useDispatch } from 'react-redux'

interface DownloadSettingsProps {
  showSettings?: boolean
}

const defaultDownloadSelection = {
  value: DEFAULT_DOWNLOAD_OPTION.value,
  label: DEFAULT_DOWNLOAD_OPTION.label
}

export const DesktopTabbedDownloadSettings = ({
  showSettings = true
}: DownloadSettingsProps): JSX.Element => {
  const [showSettingsOptions, setShowSettingsOptions] = useState(false)
  const dispatch = useDispatch()
  const {
    displayTransparencyToggle,
    transparent: isTransparent,
    downloadFileType,
    flowcodeDownloadOptions
  } = useCreateFlowcodeState()
  const { setDownloadFileType, toggleTransparency } = useCreateFlowcodeStateActions()

  return (
    <>
      <ConditionalRender condition={showSettings}>
        <Box justifyContent="space-between">
          <Box>
            <Text variant="body/small" fontSize={'14px'} marginBottom="6px">
              Type: <b>{isTransparent ? `${downloadFileType} + transparency` : downloadFileType}</b>
            </Text>
            <Tooltip
              id={`hp-${HeapContainers.CREATE_FLOWCODE}-download_file_type-tooltip`}
              margin="4px 0 0 4px"
              label="File Type"
              layout="up"
              minHeight="155px"
              content={FILE_TYPE_TOOLTIPS[downloadFileType]}
              buttonImage={questionMarkBlue}
              buttonProps={{
                type: 'button'
              }}
            />
          </Box>
          <Button
            colorVariant="invisible"
            onClick={() => setShowSettingsOptions(prevState => !prevState)}
            width="auto"
            type="button"
            id={`hp-${HeapContainers.CREATE_FLOWCODE}-change_destination-toggle`}
            color="primary.flowBlue"
          >
            <Text variant="body/small" fontSize={'14px'} color="inherit" marginRight="12px">
              <b>
                <u>{showSettingsOptions ? 'Hide file type' : 'Show file type'}</u>
              </b>
            </Text>
            {showSettingsOptions ? <ArrowUp fontSize="12px" /> : <ArrowDown fontSize="12px" />}
          </Button>
        </Box>
      </ConditionalRender>

      <ConditionalRender condition={showSettingsOptions}>
        <Box justifyContent="space-between" alignItems="center">
          <Select
            name="fileType"
            selectContainerProps={{
              width: '129px',
              backgroundColor: 'white'
            }}
            options={flowcodeDownloadOptions}
            onChange={(option: SelectOption) => {
              const fileTypeToDownload = option.value as FlowcodeDownloadOptionMimeType
              dispatch(updateFileTypeToDownload(fileTypeToDownload))
              setDownloadFileType(fileTypeToDownload)
            }}
            value={
              downloadFileType
                ? {
                    value: downloadFileType,
                    label: FlowcodeDownloadOptionMimeLabel[downloadFileType]
                  }
                : defaultDownloadSelection
            }
          />
          {displayTransparencyToggle && (
            <Box display="flex" alignItems="center" width="calc(100% - 173px)">
              <Box width="30px">
                <CheckBox
                  checked={isTransparent}
                  onChange={e => {
                    dispatch(updateFlowcodeOptionsTransparency(e.target.checked))
                    toggleTransparency(e.target.checked)
                  }}
                />
              </Box>
              <Text marginLeft="20px" variant="body/small" fontSize={'16px'}>
                Transparent Background
              </Text>
            </Box>
          )}
        </Box>
      </ConditionalRender>
    </>
  )
}

export type CreateFlowcodeDesktopProps = CreateFlowcodeContainerState

export const CreateFlowcodeFormDesktop: FC = () => {
  return (
    <Section display="flex" minHeight="auto" width="100%" backgroundColor="none">
      <SimplifiedDesktop />
    </Section>
  )
}
